/**
 * @fileoverview Source-agnostic table for displaying and selecting products for import.
 * Used by Baselinker import, Shopify import, and other product import features.
 * @module shared/components/ProductImportTable
 */

import { Fragment, useState, useMemo, useCallback, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Package,
  Search,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Loader2,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { formatMoney } from '@/shared/utils/money'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

/**
 * Generic variant preview item for import.
 */
export interface ImportVariantItem {
  /** Unique variant identifier (external ID) */
  variantId: string
  /** Variant name/title */
  name: string
  /** SKU (may be null for some sources) */
  sku: string | null
  /** Variant image URL (may be null) */
  imageUrl?: string | null
  /** Whether this variant already exists in the system */
  alreadyExists: boolean
  /** Optional cost for display */
  cost?: number | null
  /** Optional currency code for cost */
  costCurrency?: string | null
}

/**
 * Details about a sibling variant conflict.
 */
export interface SiblingConflictDetail {
  productId: number
  productName: string
  matchedVariantSkus: string[]
}

/**
 * Generic product preview item for import.
 * Works with both Baselinker and Shopify product structures.
 */
export interface ImportProductItem {
  /** Unique product identifier (external ID from source) */
  externalProductId: string
  /** Product name */
  productName: string
  /** Product image URL */
  imageUrl: string | null
  /** Product-level SKU (Baselinker) */
  sku: string | null
  /** Whether product-level SKU already exists (Baselinker) */
  skuAlreadyExists: boolean
  /** Whether all variants already exist (Shopify) */
  allVariantsExist?: boolean
  /** Whether this is a simple product with only an implicit default variant (Shopify) */
  hasOnlyDefaultVariant?: boolean
  /** Number of variants */
  variantCount: number
  /** Variant details (may be null if not expanded) */
  variants: ImportVariantItem[] | null
  /** Average cost across variants (for display) */
  averageCost: number | null
  /** Whether there's a sibling conflict */
  siblingConflict: boolean
  /** Details about the sibling conflict */
  siblingConflictDetails: SiblingConflictDetail[] | null
}

export interface ProductImportTableProps {
  /** Products to display */
  products: ImportProductItem[]
  /** Currency code for cost formatting (required if showCostColumn is true) */
  currency?: string | null
  /** Currently selected product IDs (whole products) */
  selectedProductIds: Set<string>
  /** Currently selected variant identifiers (individual variants) */
  selectedVariantIds: Set<string>
  /** Callback when product selection changes */
  onProductSelectionChange: (ids: Set<string>) => void
  /** Callback when variant selection changes */
  onVariantSelectionChange: (ids: Set<string>) => void
  /** Whether interactions are enabled */
  isActive?: boolean
  /** Whether data is loading */
  isLoading?: boolean
  /** Whether there was an error loading data */
  isError?: boolean
  /** Show cost column */
  showCostColumn?: boolean
  /** Show status column (new/partial/imported) */
  showStatusColumn?: boolean
  /** Show search input (internal search) */
  showSearch?: boolean
  /** External search term (for when parent controls search) */
  searchTerm?: string
  /** Enable variant-level selection (if false, only product-level selection) */
  enableVariantSelection?: boolean
  /** Show toggle for existing products */
  showExistingToggle?: boolean
  /** Maximum height of the table container */
  maxHeight?: string
  /** Remove border and rounded corners from table container (for embedding in styled parents) */
  noBorder?: boolean
  /** Actions to render in header (e.g., "Select all" button) */
  headerActions?: React.ReactNode
  /** Callback for "Select all" button in table header */
  onSelectAll?: () => void
  /** Callback for "Clear all" button in table header */
  onClearAll?: () => void
  /** Custom class name for the container */
  className?: string
  /** Total products from preview response (for empty state messaging) */
  totalProducts?: number
  /** Already imported count from preview response (for empty state messaging) */
  alreadyImported?: number
  /** Source name for empty state messages (e.g., "Baselinker", "Shopify") */
  sourceName?: string
  /** Function to get variant identifier for selection (defaults to SKU) */
  getVariantIdentifier?: (variant: ImportVariantItem) => string
}

type SelectionState = 'checked' | 'unchecked' | 'indeterminate'

/**
 * Default variant identifier function - uses SKU if available, otherwise variantId.
 */
const defaultGetVariantIdentifier = (variant: ImportVariantItem): string =>
  variant.sku ?? variant.variantId

/**
 * Source-agnostic table component for displaying and selecting products for import.
 * Supports product and variant-level selection, expandable rows, search, and various display options.
 */
export function ProductImportTable({
  products,
  currency,
  selectedProductIds,
  selectedVariantIds,
  onProductSelectionChange,
  onVariantSelectionChange,
  isActive = true,
  isLoading = false,
  isError = false,
  showCostColumn = false,
  showStatusColumn = false,
  showSearch = false,
  enableVariantSelection = false,
  showExistingToggle = false,
  maxHeight = '400px',
  noBorder = false,
  headerActions,
  onSelectAll,
  onClearAll,
  className,
  searchTerm: externalSearchTerm,
  totalProducts,
  alreadyImported,
  sourceName = 'external',
  getVariantIdentifier = defaultGetVariantIdentifier,
}: ProductImportTableProps): ReactElement {
  const { t } = useTranslation('features-products')
  const [internalSearchTerm, setInternalSearchTerm] = useState('')
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set())
  const [showExistingProducts, setShowExistingProducts] = useState(false)

  // Use external search term if provided, otherwise use internal state
  const searchTerm = externalSearchTerm ?? internalSearchTerm

  const toggleExpanded = useCallback((productId: string): void => {
    setExpandedProducts(prev => {
      const newSet = new Set(prev)
      if (newSet.has(productId)) {
        newSet.delete(productId)
      } else {
        newSet.add(productId)
      }
      return newSet
    })
  }, [])

  const isProductFullyExisting = useCallback((p: ImportProductItem): boolean => {
    // Shopify: use allVariantsExist flag
    if (p.allVariantsExist !== undefined) {
      return p.allVariantsExist
    }
    // Baselinker: check skuAlreadyExists and variants
    const variantExistsCount = p.variants?.filter(v => v.alreadyExists).length ?? 0
    const totalVariants = p.variants?.length ?? 0
    const hasVariants = totalVariants > 0
    const hasImportableVariants = totalVariants > variantExistsCount
    return (p.skuAlreadyExists ?? false) && !hasImportableVariants && (!hasVariants || variantExistsCount === totalVariants)
  }, [])

  const getProductStatus = useCallback((p: ImportProductItem): number => {
    // Shopify: use allVariantsExist flag
    if (p.allVariantsExist !== undefined) {
      return p.allVariantsExist ? 2 : 0
    }
    // Baselinker: check skuAlreadyExists and variants
    const variantExistsCount = p.variants?.filter(v => v.alreadyExists).length ?? 0
    const totalVariants = p.variants?.length ?? 0
    const hasVariants = totalVariants > 0
    const hasImportableVariants = totalVariants > variantExistsCount
    const isPartial = hasVariants && variantExistsCount > 0 && hasImportableVariants
    const isFullyExists = (p.skuAlreadyExists ?? false) && !hasImportableVariants

    if (isFullyExists) return 2
    if (isPartial) return 1
    return 0
  }, [])

  const getProductSelectionState = useCallback((product: ImportProductItem): SelectionState => {
    if (selectedProductIds.has(product.externalProductId)) {
      return 'checked'
    }
    if (!enableVariantSelection) {
      return 'unchecked'
    }
    const importableVariants = product.variants?.filter(v => !v.alreadyExists) ?? []
    if (importableVariants.length === 0) return 'unchecked'

    const selectedCount = importableVariants.filter(v => selectedVariantIds.has(getVariantIdentifier(v))).length
    if (selectedCount === 0) return 'unchecked'
    if (selectedCount === importableVariants.length) return 'checked'
    return 'indeterminate'
  }, [selectedProductIds, selectedVariantIds, enableVariantSelection, getVariantIdentifier])

  const isVariantSelected = useCallback((productId: string, variant: ImportVariantItem): boolean => {
    return selectedProductIds.has(productId) || selectedVariantIds.has(getVariantIdentifier(variant))
  }, [selectedProductIds, selectedVariantIds, getVariantIdentifier])

  const handleToggleProduct = useCallback((productId: string, checked: boolean): void => {
    const newProductIds = new Set(selectedProductIds)
    const newVariantIds = new Set(selectedVariantIds)
    const product = products.find(p => p.externalProductId === productId)

    if (checked) {
      if (enableVariantSelection) {
        const importableVariants = product?.variants?.filter(v => !v.alreadyExists) ?? []
        const hasVariants = (product?.variants?.length ?? 0) > 0
        const isPartial = hasVariants && importableVariants.length < (product?.variants?.length ?? 0)

        if (isPartial) {
          importableVariants.forEach(v => newVariantIds.add(getVariantIdentifier(v)))
        } else {
          newProductIds.add(productId)
          product?.variants?.forEach(v => newVariantIds.delete(getVariantIdentifier(v)))
        }
      } else {
        newProductIds.add(productId)
      }
    } else {
      newProductIds.delete(productId)
      if (enableVariantSelection) {
        product?.variants?.forEach(v => newVariantIds.delete(getVariantIdentifier(v)))
      }
    }

    onProductSelectionChange(newProductIds)
    onVariantSelectionChange(newVariantIds)
  }, [products, selectedProductIds, selectedVariantIds, enableVariantSelection, onProductSelectionChange, onVariantSelectionChange, getVariantIdentifier])

  const handleToggleVariant = useCallback((productId: string, variant: ImportVariantItem, checked: boolean): void => {
    if (!enableVariantSelection) return

    const variantIdentifier = getVariantIdentifier(variant)
    const newVariantIds = new Set(selectedVariantIds)
    const newProductIds = new Set(selectedProductIds)

    if (selectedProductIds.has(productId)) {
      newProductIds.delete(productId)
      const product = products.find(p => p.externalProductId === productId)
      product?.variants?.forEach(v => {
        if (getVariantIdentifier(v) !== variantIdentifier && !v.alreadyExists) {
          newVariantIds.add(getVariantIdentifier(v))
        }
      })
    }

    if (checked) {
      newVariantIds.add(variantIdentifier)
    } else {
      newVariantIds.delete(variantIdentifier)
    }

    onProductSelectionChange(newProductIds)
    onVariantSelectionChange(newVariantIds)
  }, [products, selectedProductIds, selectedVariantIds, enableVariantSelection, onProductSelectionChange, onVariantSelectionChange, getVariantIdentifier])

  const hiddenExistingCount = useMemo(() => {
    if (!showExistingToggle || showExistingProducts) return 0
    return products.filter(p => {
      const term = searchTerm.toLowerCase()
      const matchesSearch = p.productName.toLowerCase().includes(term) ||
        (p.sku && p.sku.toLowerCase().includes(term)) ||
        p.variants?.some(v => v.name.toLowerCase().includes(term) || (v.sku && v.sku.toLowerCase().includes(term)))
      if (!matchesSearch) return false
      return isProductFullyExisting(p)
    }).length
  }, [products, searchTerm, showExistingProducts, showExistingToggle, isProductFullyExisting])

  const filteredProducts = useMemo(() => {
    return products
      .filter(p => {
        const term = searchTerm.toLowerCase()
        if (!p.productName.toLowerCase().includes(term) &&
            !(p.sku && p.sku.toLowerCase().includes(term)) &&
            !p.variants?.some(v =>
              v.name.toLowerCase().includes(term) ||
              (v.sku && v.sku.toLowerCase().includes(term))
            )) {
          return false
        }
        if (showExistingToggle && !showExistingProducts && isProductFullyExisting(p)) {
          return false
        }
        return true
      })
      .sort((a, b) => getProductStatus(a) - getProductStatus(b))
  }, [products, searchTerm, showExistingToggle, showExistingProducts, isProductFullyExisting, getProductStatus])

  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className={cn('flex items-center gap-2 text-sm text-destructive py-4', className)}>
        <AlertCircle className="size-4" />
        {t('import.preview.error')}
      </div>
    )
  }

  if (products.length === 0) {
    // Case 1: All products already imported
    if (totalProducts !== undefined && totalProducts > 0 && totalProducts === alreadyImported) {
      return (
        <div className={cn('flex flex-col items-center justify-center gap-2 text-center', className)}>
          <CheckCircle2 className="h-10 w-10 text-primary" />
          <p className="text-sm text-muted-foreground">
            {t('import.allImported', 'All products are already imported')}
          </p>
        </div>
      )
    }

    // Case 2: No products in source
    if (totalProducts !== undefined && totalProducts === 0) {
      return (
        <div className={cn('flex flex-col items-center justify-center gap-3 text-center', className)}>
          <Package className="h-10 w-10 text-muted-foreground" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">
              {t('import.noProductsFound', { source: sourceName, defaultValue: `No ${sourceName} products found` })}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('import.noProductsHint', { source: sourceName, defaultValue: `Please select another source or check if they exist in ${sourceName}` })}
            </p>
          </div>
        </div>
      )
    }

    // Default fallback
    return (
      <div className={cn('flex flex-col items-center justify-center gap-2 text-center', className)}>
        <Package className="h-10 w-10 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          {t('import.noProducts', 'No products to display')}
        </p>
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col', className)}>
      {(showSearch || headerActions) && (
        <div className="flex items-center gap-4 mb-4">
          {showSearch && (
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('import.searchPlaceholder', 'Search products...')}
                value={internalSearchTerm}
                onChange={(e) => setInternalSearchTerm(e.target.value)}
                className="pl-8 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </div>
          )}
          {headerActions}
        </div>
      )}

      <div
        className={cn(
          'flex-1 overflow-auto scrollbar-elegant',
          !noBorder && 'border border-border/40 rounded-xl'
        )}
        style={{ maxHeight }}
      >
        <Table className="table-fixed w-full">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[70px] sm:w-[90px] border-r border-border/40">
                {selectedProductIds.size > 0 || selectedVariantIds.size > 0 ? (
                  onClearAll && (
                    <Button variant="ghost" size="sm" onClick={onClearAll} className="h-7 text-xs sm:text-sm text-muted-foreground px-1 sm:px-2">
                      {t('import.table.clearAll', 'Clear all')}
                    </Button>
                  )
                ) : (
                  onSelectAll && (
                    <Button variant="ghost" size="sm" onClick={onSelectAll} className="h-7 text-xs sm:text-sm text-muted-foreground px-1 sm:px-2">
                      {t('import.table.selectAll', 'Select all')}
                    </Button>
                  )
                )}
              </TableHead>
              <TableHead className="pl-2 sm:pl-4">{t('import.table.name')}</TableHead>
              {showCostColumn && (
                <TableHead className="w-[100px] text-right pr-6">{t('import.table.cost')}</TableHead>
              )}
              {showStatusColumn && (
                <TableHead className="w-[80px]">{t('import.table.status')}</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredProducts.map((product) => {
              // Simple products (hasOnlyDefaultVariant) should not show variant rows
              const hasVariants = (product.variants?.length ?? 0) > 0 && !product.hasOnlyDefaultVariant
              const isExpanded = expandedProducts.has(product.externalProductId)
              const selectionState = getProductSelectionState(product)

              const variantExistsCount = product.variants?.filter(v => v.alreadyExists).length ?? 0
              const totalVariants = product.variants?.length ?? 0
              const hasImportableVariants = totalVariants > variantExistsCount
              const isPartial = hasVariants && variantExistsCount > 0 && hasImportableVariants
              // Use allVariantsExist for Shopify, skuAlreadyExists for Baselinker
              const isFullyExists = product.allVariantsExist ?? ((product.skuAlreadyExists ?? false) && !hasImportableVariants)

              return (
                <Fragment key={product.externalProductId}>
                  <TableRow
                    data-selected={selectionState !== 'unchecked' ? 'true' : 'false'}
                    className={cn(
                      'cursor-pointer [&_td]:py-2 sm:[&_td]:py-3',
                      isFullyExists && 'opacity-50 cursor-not-allowed',
                      product.siblingConflict && 'bg-warning/5 border-l-2 border-l-warning cursor-not-allowed',
                      selectionState !== 'unchecked' && !product.siblingConflict && 'bg-secondary'
                    )}
                    onClick={() => {
                      if (!isFullyExists && !product.siblingConflict && isActive) {
                        handleToggleProduct(product.externalProductId, selectionState === 'unchecked')
                      }
                    }}
                  >
                    <TableCell className="border-r border-border/40 p-1 sm:p-2">
                      <div className="flex items-center gap-1 sm:gap-3">
                        {hasVariants ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleExpanded(product.externalProductId)
                            }}
                            className="flex h-6 w-6 sm:h-8 sm:w-8 items-center justify-center rounded-md border border-border/50 hover:bg-muted/50 transition-colors"
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                            )}
                          </button>
                        ) : (
                          <div className="w-6 sm:w-8" />
                        )}
                        <Checkbox
                          checked={selectionState === 'checked' ? true : selectionState === 'indeterminate' ? 'indeterminate' : false}
                          onCheckedChange={(checked: boolean) => handleToggleProduct(product.externalProductId, checked)}
                          disabled={!isActive || isFullyExists || product.siblingConflict}
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          className="!border-white/15 h-4 w-4 sm:h-5 sm:w-5"
                        />
                      </div>
                    </TableCell>
                    <TableCell className="pl-2 sm:pl-4 p-1 sm:p-2">
                      <div className="flex items-center gap-2 sm:gap-3">
                        {product.imageUrl ? (
                          <img src={product.imageUrl} alt="" className="h-8 w-8 sm:h-10 sm:w-10 aspect-square object-contain rounded-md flex-shrink-0" />
                        ) : (
                          <div className="h-8 w-8 sm:h-10 sm:w-10 aspect-square bg-muted rounded-md flex items-center justify-center flex-shrink-0">
                            <Package className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                          </div>
                        )}
                        <div className="min-w-0 overflow-hidden">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium whitespace-normal break-words text-sm sm:text-base">{product.productName}</p>
                            {product.variantCount > 0 && !product.hasOnlyDefaultVariant && (
                              <span className="inline-flex items-center rounded-full bg-muted/50 px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-[11px] text-muted-foreground flex-shrink-0">
                                {product.variantCount} {t('import.table.variantsBadge', 'variants')}
                              </span>
                            )}
                          </div>
                          {product.sku && <p className="text-[10px] sm:text-xs text-muted-foreground truncate">{product.sku}</p>}
                        </div>
                      </div>
                    </TableCell>
                    {showCostColumn && (
                      <TableCell className="text-right tabular-nums pr-6 text-xs text-muted-foreground">
                        {product.averageCost != null && currency ? formatMoney(product.averageCost, currency, undefined, 2, 2, 'narrowSymbol') : '—'}
                      </TableCell>
                    )}
                    {showStatusColumn && (
                      <TableCell className="border-l border-border/40">
                        {product.siblingConflict ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-1.5 text-warning cursor-help">
                                <AlertTriangle className="h-4 w-4" />
                                <span className="text-xs font-medium">{t('import.status.conflict', 'Conflict')}</span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-sm">
                              <div className="space-y-2">
                                <p className="font-medium text-sm">{t('import.conflict.title', 'Sibling variants mapped to different products')}</p>
                                <p className="text-xs text-muted-foreground">
                                  {t('import.conflict.description', 'Variants from this product exist in multiple internal products. Resolve by merging products or manually assigning variants.')}
                                </p>
                                {product.siblingConflictDetails && (
                                  <ul className="text-xs space-y-1 mt-2">
                                    {product.siblingConflictDetails.map((detail) => (
                                      <li key={detail.productId} className="flex flex-col">
                                        <span className="font-medium">{detail.productName}</span>
                                        <span className="text-muted-foreground">{detail.matchedVariantSkus.join(', ')}</span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {isFullyExists
                              ? t('import.status.imported')
                              : isPartial
                                ? t('import.status.partial')
                                : t('import.status.new')}
                          </span>
                        )}
                      </TableCell>
                    )}
                  </TableRow>

                  {/* Variant rows */}
                  {hasVariants && isExpanded && product.variants?.map((variant) => {
                    const variantSelected = isVariantSelected(product.externalProductId, variant)
                    return (
                      <TableRow
                        key={variant.variantId}
                        data-selected={variantSelected ? 'true' : 'false'}
                        className={cn(
                          'bg-black/20 hover:bg-black/20 cursor-pointer [&_td]:py-2 sm:[&_td]:py-3',
                          variant.alreadyExists && 'opacity-50 cursor-not-allowed',
                          variantSelected && 'bg-secondary'
                        )}
                        onClick={() => {
                          if (!variant.alreadyExists && isActive && enableVariantSelection) {
                            handleToggleVariant(product.externalProductId, variant, !variantSelected)
                          }
                        }}
                      >
                        <TableCell className="p-1 sm:p-2">
                          <div className="ml-2 sm:ml-4 flex items-center gap-2 border-l border-border/40 pl-2 sm:pl-4">
                            <Checkbox
                              checked={variantSelected}
                              onCheckedChange={(checked: boolean) => handleToggleVariant(product.externalProductId, variant, checked)}
                              disabled={!isActive || variant.alreadyExists || !enableVariantSelection}
                              onClick={(e: React.MouseEvent) => e.stopPropagation()}
                              className="!border-white/15 h-4 w-4 sm:h-5 sm:w-5"
                            />
                          </div>
                        </TableCell>
                        <TableCell className="pl-2 sm:pl-4 p-1 sm:p-2">
                          <div className="ml-2 sm:ml-4 flex items-center gap-2 sm:gap-3 border-l border-border/40 pl-4 sm:pl-10 min-w-0 overflow-hidden">
                            {variant.imageUrl ? (
                              <img src={variant.imageUrl} alt="" className="h-6 w-6 sm:h-8 sm:w-8 aspect-square object-contain rounded flex-shrink-0" />
                            ) : (
                              <div className="h-6 w-6 sm:h-8 sm:w-8 aspect-square bg-muted rounded flex items-center justify-center flex-shrink-0">
                                <Package className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
                              </div>
                            )}
                            <div className="flex flex-col gap-0.5 min-w-0 overflow-hidden">
                              <p className="text-xs sm:text-sm break-words whitespace-normal">{variant.name}</p>
                              {variant.sku && <p className="text-[10px] sm:text-xs text-muted-foreground truncate">{variant.sku}</p>}
                            </div>
                          </div>
                        </TableCell>
                        {showCostColumn && <TableCell />}
                        {showStatusColumn && (
                          <TableCell>
                            <span className="text-xs text-muted-foreground">
                              {variant.alreadyExists ? t('import.status.imported') : t('import.status.new')}
                            </span>
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  })}
                </Fragment>
              )
            })}

            {/* Show existing products toggle row */}
            {showExistingToggle && hiddenExistingCount > 0 && (
              <TableRow
                className="cursor-pointer hover:bg-muted/50 border-t-2 border-dashed border-border/40"
                onClick={() => setShowExistingProducts(true)}
              >
                <TableCell colSpan={2 + (showCostColumn ? 1 : 0) + (showStatusColumn ? 1 : 0)} className="text-center py-4">
                  <span className="text-sm text-muted-foreground">
                    {t('import.showExisting', { count: hiddenExistingCount, defaultValue: `Show ${hiddenExistingCount} existing products` })}
                  </span>
                </TableCell>
              </TableRow>
            )}
            {showExistingToggle && showExistingProducts && products.some(isProductFullyExisting) && (
              <TableRow
                className="cursor-pointer hover:bg-muted/50 border-t-2 border-dashed border-border/40"
                onClick={() => setShowExistingProducts(false)}
              >
                <TableCell colSpan={2 + (showCostColumn ? 1 : 0) + (showStatusColumn ? 1 : 0)} className="text-center py-4">
                  <span className="text-sm text-muted-foreground">
                    {t('import.hideExisting', { defaultValue: 'Hide existing products' })}
                  </span>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
